import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type {
  InsightsRevenueBridgeMover,
  InsightsRevenueBridgeResponse,
} from "@shared-core/data/api-types";
import {
  currentMargin,
  formatGbpCompact,
  formatMarginPct,
  formatSignedGbpCompact,
  formatSignedPp,
  funnelRows,
  gainedSide,
  marginShiftPct,
  movedCustomerCount,
  netDelta,
  priorMargin,
  rateVolumeSplit,
  shedSide,
  thesisText,
  toWaterfallSteps,
  type BridgeMeasure,
  type FunnelRow,
  type WaterfallStep,
} from "@shared-core/domain/insights-revenue-bridge";

// Revenue Bridge — the margin bridge. Faithful to the settled design reference
// (docs/features/bi-native-analysis/charts/q2-bridge.html), drawn on the
// footgun-safe trailing-12-months-vs-prior-year basis (NOT the design's literal
// Q2-vs-Q2 pair): the endpoint reads customer_revenue's TTM/prior windows, so
// lumpy accounts don't mislead. The whole story is the asymmetry between the two
// measures — the business swaps low-margin revenue for high-margin revenue, so
// revenue can be ~flat while contribution jumps — which is why BOTH waterfalls
// are shown side-by-side rather than behind a toggle.
//
// Server composes, client renders: the endpoint returns the classified cohort
// totals, the ranked movers, and the estimate funnel already summed; this
// component only projects them into geometry + prose via the headless selectors.

const GAIN_COLOR = "var(--color-chart-2)";
const LOSS_COLOR = "var(--color-destructive)";
const ANCHOR_COLOR = "var(--color-muted-foreground)";

export interface RevenueBridgeChartProps {
  bridge: InsightsRevenueBridgeResponse;
}

export function RevenueBridgeChart({ bridge }: RevenueBridgeChartProps) {
  const hasData =
    bridge.prior_revenue_total !== 0 ||
    bridge.current_revenue_total !== 0 ||
    bridge.buckets.some((b) => b.revenue_delta !== 0);

  if (!hasData) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No customer revenue data yet.
      </div>
    );
  }

  const revNet = netDelta(bridge, "revenue");
  const conNet = netDelta(bridge, "contribution");
  const shift = marginShiftPct(bridge);
  const moved = movedCustomerCount(bridge);

  return (
    <div className="space-y-4">
      <header className="space-y-2">
        <p className="text-muted-foreground text-[11px] font-semibold tracking-wider uppercase">
          Live production · trailing 12 months vs prior year · invoiced ·
          grouped, intercompany excluded
        </p>
        <h2 className="text-foreground text-xl font-semibold">
          The margin bridge — what changed, and why
        </h2>
        <p className="border-primary bg-surface text-foreground max-w-[80ch] rounded-lg border border-l-[3px] p-3 text-sm">
          {thesisText(bridge)}
        </p>
      </header>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi
          label="Net revenue Δ"
          value={formatSignedGbpCompact(revNet)}
          tone={revNet >= 0 ? "up" : "down"}
          sub={`was ${formatGbpCompact(bridge.prior_revenue_total)}`}
        />
        <Kpi
          label="Net contribution Δ"
          value={formatSignedGbpCompact(conNet)}
          tone={conNet >= 0 ? "up" : "down"}
          sub={`was ${formatGbpCompact(bridge.prior_contribution_total)}`}
        />
        <Kpi
          label="Margin-mix shift"
          value={shift == null ? "—" : formatSignedPp(shift)}
          sub={`${formatMarginPct(priorMargin(bridge))} → ${formatMarginPct(currentMargin(bridge))}`}
          tone={shift == null ? undefined : shift >= 0 ? "up" : "down"}
        />
        <Kpi
          label="Customers moved"
          value={String(moved)}
          sub="new + grown + shrunk + lost"
        />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <WaterfallPanel
          bridge={bridge}
          measure="revenue"
          title="Revenue bridge"
          caption={revenueCaption(bridge)}
        />
        <WaterfallPanel
          bridge={bridge}
          measure="contribution"
          title="Contribution bridge"
          caption={contributionCaption(bridge)}
        />
      </div>

      <CalloutBand bridge={bridge} />

      <FunnelPanel bridge={bridge} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <MoversPanel title="Top gainers" rows={bridge.gainers} />
        <MoversPanel title="Top decliners" rows={bridge.decliners} />
      </div>

      <Footnote />
    </div>
  );
}

function revenueCaption(bridge: InsightsRevenueBridgeResponse): string {
  const net = netDelta(bridge, "revenue");
  const flat = Math.abs(net) < 0.02 * (bridge.prior_revenue_total || 1);
  return `${formatGbpCompact(bridge.prior_revenue_total)} → ${formatGbpCompact(
    bridge.current_revenue_total,
  )}${flat ? " — essentially flat. Gains and churn almost offset." : "."}`;
}

function contributionCaption(bridge: InsightsRevenueBridgeResponse): string {
  const prior = bridge.prior_contribution_total;
  const pct = prior
    ? Math.round((100 * netDelta(bridge, "contribution")) / prior)
    : null;
  return `${formatGbpCompact(prior)} → ${formatGbpCompact(
    bridge.current_contribution_total,
  )}${pct == null ? "." : ` (${pct >= 0 ? "+" : ""}${pct}%). What was lost barely dents contribution.`}`;
}

function WaterfallPanel({
  bridge,
  measure,
  title,
  caption,
}: {
  bridge: InsightsRevenueBridgeResponse;
  measure: BridgeMeasure;
  title: string;
  caption: string;
}) {
  const steps = toWaterfallSteps(bridge, measure);
  // Recharts stacks two bars: an invisible spacer up to `base`, then the
  // visible bar of height (top − base). Anchors sit on the floor (base 0).
  const data = steps.map((s) => ({
    ...s,
    spacer: s.base,
    magnitude: s.top - s.base,
  }));

  return (
    <div className="border-border bg-surface rounded-lg border p-4">
      <h3 className="text-foreground text-sm font-semibold">{title}</h3>
      <p className="text-muted-foreground mb-3 text-xs">{caption}</p>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart
          data={data}
          margin={{ top: 24, right: 12, bottom: 8, left: 8 }}
        >
          <CartesianGrid stroke="var(--color-border)" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
            axisLine={{ stroke: "var(--color-border)" }}
            tickLine={false}
          />
          <YAxis
            tickFormatter={formatGbpCompact}
            tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
            axisLine={false}
            tickLine={false}
            width={56}
          />
          <Tooltip
            cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
            content={BridgeTooltip as never}
          />
          {/* Invisible spacer lifts the visible bar to its floating base. */}
          <Bar
            dataKey="spacer"
            stackId="wf"
            fill="transparent"
            isAnimationActive={false}
          />
          <Bar
            dataKey="magnitude"
            stackId="wf"
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          >
            {data.map((d) => (
              <Cell key={d.key} fill={stepColor(d)} />
            ))}
            <LabelList dataKey="value" content={StepLabel as never} />
          </Bar>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function stepColor(step: WaterfallStep): string {
  if (step.kind === "anchor") return ANCHOR_COLOR;
  return step.value >= 0 ? GAIN_COLOR : LOSS_COLOR;
}

// Value label above each bar — the total for anchors, the signed delta for steps.
function StepLabel(props: {
  x?: number;
  y?: number;
  width?: number;
  index?: number;
  value?: number;
}) {
  const { x = 0, y = 0, width = 0, index, value } = props;
  if (index == null || value == null) return null;
  const isAnchor = index === 0 || index === 5;
  const text = isAnchor
    ? formatGbpCompact(value)
    : formatSignedGbpCompact(value);
  return (
    <text
      x={x + width / 2}
      y={y - 6}
      textAnchor="middle"
      className="fill-foreground text-[10px] font-medium tabular-nums"
    >
      {text}
    </text>
  );
}

function BridgeTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0)
    return null;
  const d = props.payload[0]?.payload as WaterfallStep | undefined;
  if (!d) return null;
  const valueText =
    d.kind === "anchor"
      ? formatGbpCompact(d.value)
      : formatSignedGbpCompact(d.value);
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">{d.label}</div>
      <TooltipRow
        label={d.kind === "anchor" ? "Total" : "Change"}
        value={valueText}
      />
      {d.customerCount != null ? (
        <TooltipRow label="Customers" value={String(d.customerCount)} />
      ) : null}
    </div>
  );
}

function TooltipRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}

// The margin-weighted callout: gained @ margin · shed @ margin · rate vs volume.
function CalloutBand({ bridge }: { bridge: InsightsRevenueBridgeResponse }) {
  const gained = gainedSide(bridge);
  const shed = shedSide(bridge);
  const { rate, volume } = rateVolumeSplit(bridge);
  return (
    <div className="border-border bg-surface flex flex-wrap items-center gap-6 rounded-lg border p-4">
      <CalloutSeg
        swatch={GAIN_COLOR}
        label="Revenue gained (new + grown)"
        value={
          <>
            {formatSignedGbpCompact(gained.revenueDelta)} @{" "}
            <span className="text-chart-2">
              {formatMarginPct(gained.margin)} margin
            </span>
          </>
        }
      />
      <CalloutSeg
        swatch={LOSS_COLOR}
        label="Revenue shed (lost + shrunk)"
        value={
          <>
            {formatSignedGbpCompact(shed.revenueDelta)} @{" "}
            <span className="text-destructive">
              {formatMarginPct(shed.margin)} margin
            </span>
          </>
        }
      />
      <CalloutSeg
        label="Contribution change = rate, not volume"
        value={
          <>
            {formatSignedGbpCompact(rate)} rate ·{" "}
            {formatSignedGbpCompact(volume)} volume
          </>
        }
      />
    </div>
  );
}

function CalloutSeg({
  swatch,
  label,
  value,
}: {
  swatch?: string;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-[11px] tracking-wide uppercase">
        {swatch ? (
          <span
            className="mr-1.5 inline-block h-2.5 w-2.5 rounded-[2px] align-middle"
            style={{ background: swatch }}
          />
        ) : null}
        {label}
      </span>
      <span className="text-foreground font-mono text-sm font-semibold">
        {value}
      </span>
    </div>
  );
}

// The estimate funnel — the leading indicator, current TTM vs prior TTM.
function FunnelPanel({ bridge }: { bridge: InsightsRevenueBridgeResponse }) {
  const rows = funnelRows(bridge.funnel);
  return (
    <div className="border-border bg-surface rounded-lg border p-4">
      <h3 className="text-foreground text-sm font-semibold">
        The leading indicator — estimate funnel
      </h3>
      <p className="text-muted-foreground mb-3 text-xs">
        The upstream cause: fewer, better-qualified quotes. Quoting fell but the
        hit rate rose.
      </p>
      <div className="flex flex-wrap gap-x-8 gap-y-4">
        {rows.map((row) => (
          <FunnelItem key={row.key} row={row} />
        ))}
      </div>
    </div>
  );
}

function FunnelItem({ row }: { row: FunnelRow }) {
  const changeClass =
    row.direction > 0
      ? "text-chart-2"
      : row.direction < 0
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <div className="text-muted-foreground text-xs">
      {row.label}
      <span className="text-foreground mt-0.5 block font-mono text-lg font-semibold">
        {row.valueText}{" "}
        <span className={`text-xs ${changeClass}`}>{row.changeText}</span>
      </span>
    </div>
  );
}

function MoversPanel({
  title,
  rows,
}: {
  title: string;
  rows: InsightsRevenueBridgeMover[];
}) {
  return (
    <div className="border-border bg-surface rounded-lg border p-4">
      <h3 className="text-foreground mb-2 text-sm font-semibold">{title}</h3>
      <div className="overflow-x-auto">
        {rows.length === 0 ? (
          <p className="text-muted-foreground py-4 text-center text-xs">
            No movers this period.
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead>
              <tr className="text-muted-foreground border-border border-b">
                <th className="px-2 py-1.5 text-left font-semibold tracking-wide uppercase">
                  Customer
                </th>
                <th className="px-2 py-1.5 text-right font-semibold tracking-wide uppercase">
                  Δ Revenue
                </th>
                <th className="px-2 py-1.5 text-right font-semibold tracking-wide uppercase">
                  Δ Contrib.
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => (
                <tr
                  key={m.reporting_customer_code}
                  className="border-border/50 border-b last:border-0"
                >
                  <td className="px-2 py-1.5 whitespace-nowrap">
                    {m.reporting_customer_name}
                  </td>
                  <MoverCell value={m.revenue_delta} />
                  <MoverCell value={m.contribution_delta} />
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function MoverCell({ value }: { value: number }) {
  const tone = value >= 0 ? "text-chart-2" : "text-destructive";
  return (
    <td
      className={`px-2 py-1.5 text-right font-mono tabular-nums ${tone} whitespace-nowrap`}
    >
      {formatSignedGbpCompact(value)}
    </td>
  );
}

function Footnote() {
  return (
    <p className="border-border text-muted-foreground border-t pt-3 text-[11px]">
      <span className="text-foreground font-medium">Basis:</span> invoiced
      revenue / contribution (<code>fct_chargeable_work</code>), trailing 12
      months vs the prior year — not a single quarter-pair, so lumpy accounts
      can't mislead. Grouped by posting account (HH-consolidated) and
      intercompany excluded. Cohort = new / grown / shrunk / lost; the estimate
      funnel reads the deduped <code>quoted_value</code> metric over the same
      windows.
    </p>
  );
}

function Kpi({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "up" | "down";
}) {
  const toneClass =
    tone === "up"
      ? "text-chart-2"
      : tone === "down"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="border-border bg-surface rounded-lg border p-3">
      <div className="text-muted-foreground text-xs font-medium">{label}</div>
      <div
        className={`${toneClass} mt-1.5 font-mono text-xl font-semibold tabular-nums`}
      >
        {value}
      </div>
      {sub ? (
        <div className="text-muted-foreground mt-0.5 text-xs">{sub}</div>
      ) : null}
    </div>
  );
}
